import React, {FC, useEffect, useState} from 'react';

import path_up from 'assets/images/closeSelectIcon.svg';
import path_down from 'assets/images/openSelectIcon.svg';
import iconStateDisabled from "assets/icons/commons/source-state-disabled.svg"
import iconStateSuccess from "assets/icons/commons/source-state-active.svg"
import iconStateError from "assets/icons/commons/source-state-error.svg"
import ToggleButton from "../../ToggleButton";
import {DataSource} from "../../../../constants/DataSource/DataSource";
import {DataSourceStatus} from "../../../../constants/DataSource/DataSourceStatus";
import {t} from "i18next";
import FormSelect, {ISelectOption} from "../../../commons/Select/FormSelect";
import {v4 as uuidv4} from "uuid";
import {warehouseTypeLabels} from "../../../Tables/WarehouseTable/parse-warehouse-type";
import warehousesService from "../../../../api/services/warehousesService";
import {useSelector} from "react-redux";
import {createSelector} from "reselect";
import {ISellerData} from "../../../../redux/sellers/types";
import {WarehouseType} from "../../../../constants/Warehouses/WarehouseType";
import {MenuWithButton} from "../../../commons/Select/Default/CustomSelectComponents";
import AsyncSelect from "../../../commons/Select/Async/AsyncSelect";
import SellerService from "../../../../api/services/sellerService";
import {FrequencySelector} from "../../FrequencySelector";
import classNames from "classnames";
import {RuleRow, StockConvertRule} from "./RuleRow";
import {Button} from "../../../buttons";
import plus from "../../../../assets/icons/commons/roundPlus.svg";

const initialStockConvertRule: StockConvertRule = {
  contains: '0',
  operator: '',
  count: 0,
}

interface DataSourceProps {
  code: string
  whID: string
  data: DataSource|null
  updateDataSourceHandler: (code: string, payload: DataSource|null) => void
  toggleIsDisclosed: (code: string) => void
  toggleItemStatus: (code: string, isActive: boolean) => void
  isDisclosed: boolean
  isActive: boolean
}

const memoizedSelector = createSelector(
    (state: { sellers: { isAllSellersLoaded: any; }; }) => state.sellers.isAllSellersLoaded,
    (state: { sellers: { allSellers: ISellerData[]; }; }) => state.sellers.allSellers,
    (state: { sellers: { currentSeller: any; }; }) => state.sellers.currentSeller,
    (isAllSellersLoaded, allSellers, currentSeller) => ({ isAllSellersLoaded, allSellers, currentSeller,})
);

export const Warehouse: FC<DataSourceProps> = ({
  code,
  whID,
  data,
  updateDataSourceHandler,
  toggleIsDisclosed,
  toggleItemStatus,
  isDisclosed,
  isActive
}) => {

  const title = 'Другой склад'

  const {
    isAllSellersLoaded,
    allSellers,
    currentSeller,
  } = useSelector(memoizedSelector);

  const onStatusChange = (isActiveStatus: boolean) => {
    isActive = isActiveStatus
    toggleItemStatus(code, isActive)
  };

  const [selectedSeller, setSelectedSeller] = useState<ISelectOption>({value: '1', text: ''});
  const [sellerID, setSellerID] = useState('');
  const [warehouseID, setWarehouseID] = useState('');

  // Param - frequency
  const onUpdateFrequencyChange = (frequency: number) => {
    setUpdateFrequency(frequency)
  };
  const [updateFrequency, setUpdateFrequency] = useState(0);

  const [updateWarehouseSelectorOptions, setUpdateWarehouseSelectorOptions] = useState<ISelectOption[]>([]);
  const [sellerSelectorOptions, setSellerSelectorOptions] = useState<ISelectOption[]>([]);
  const [warehouses, setWarehouses] = useState<{uuid: string, status: string, name: string, type: string, externalService: string, externalID: string}[]>([]);

  // Param - internal identity field
  const availableMapByOptions: {[key: string]: string} = {
    "id": "ID LK",
    "sku": "Артикул",
    "barcode": "Штрих-код",
  }
  const defaultMapBy = "id"
  const [mapBy, setMapBy] = useState(defaultMapBy);
  const [mapBySelectorOptions, setMapBySelectorOptions] = useState<ISelectOption[]>([]);

  const fillMapByOptions = function () {
    const isAvailableMappingByID = (currentSeller?.info?.data?.id || '').toString() === sellerID.toString()
    const mapBySelectOptions: ISelectOption[] = []
    for (const key in availableMapByOptions) {
      // Если выбираются склады текущей компании то показываем только ID, если склады другой компании, то наоборот скрываем ID
      if ((isAvailableMappingByID && key !== 'id') || (!isAvailableMappingByID && key === 'id')) {
        continue
      }
      mapBySelectOptions.push({text: availableMapByOptions[key], value: key})
    }
    setMapBy(mapBy || mapBySelectOptions[0]?.value || defaultMapBy)
    setMapBySelectorOptions(mapBySelectOptions)
  }

  // Param - stock convert rules
  const [stockConvertRules, setStockConvertRules] = useState<StockConvertRule[]>([]);

  const [errors, setErrors] = useState({warehouseID: ''});

  // @ts-ignore
  const fieldsWithErrors = Object.keys(errors).filter((k: string) => errors[k].length > 0)
  const isFormHasErrors = (isActive && !warehouseID) || fieldsWithErrors.length > 0
  const state = isActive ? (isFormHasErrors ? 'fail' : 'success') : 'disabled'
  const dataSourceStateIconAssets = {
    'disabled': iconStateDisabled,
    'success': iconStateSuccess,
    'fail': iconStateError
  }

  useEffect(() => {
    // Sellers
    const sellerSelectOptions: ISelectOption[] = []
    allSellers.map((s: ISellerData) => {
      sellerSelectOptions.push({text: `${s.id} - ${s.sellerName}`, value: s.id.toString()})
    })
    setSellerSelectorOptions(sellerSelectOptions)

    // Map by
    fillMapByOptions()
  }, [])

  useEffect(() => {
    const currentSellerId = (currentSeller?.info?.data?.id || '').toString();
    const currentSellerName = (currentSeller?.info?.data?.sellerName || '').toString();
    setSellerID(currentSellerId)
    setSelectedSeller({text: `${currentSellerId} - ${currentSellerName}`, value: currentSellerId})
  }, [currentSeller])

  useEffect(() => {
    const currentSellerId = (currentSeller?.info?.data?.id || '').toString();
    // Warehouses
    sellerID && warehousesService.getWarehousesForSeller(parseInt(sellerID))
        .then(
            (res) => {
              if (res.data.success) {
                // Фильтруем неактивные склады и исключаем непубличные склады соседних компаний
                setWarehouses(res.data.data.filter((v:any) => v.status === 'active' && (sellerID == currentSellerId || v.type === WarehouseType.PublicFBS)))
              }
            },
            (err) => {
              alert(err.response.data.error);
            }
        )
        .catch((err) => {
          alert(err.message);
        })
    ;

    // Map by
    fillMapByOptions()
  }, [sellerID])

  useEffect(() => {
    // Warehouses
    const selectOptions: ISelectOption[] = [
      {text: '', value: ''}
    ]
    warehouses.forEach(w => {
      // Исключаем из выборки сам склад
      if (w.uuid === whID) {
        return
      }

      const typeLabel = t(warehouseTypeLabels[w.type]) ?? ''
      let name = w.name
      let extID = ''
      if (w.externalService) {
        const lowerName = name.toLowerCase()
        if (w.externalService !== 'SystemPublicWH') {
          if (w.externalService.indexOf('LK') !== -1) {
            extID = `, ${w.externalID}`
          } else if (lowerName.indexOf(w.externalService.toLowerCase()) === -1) {
            name = t<string>(`Data source ${w.externalService}`, {ns: "messages"}) + ` ${name}`
            extID = `, ${w.externalID}`
          }
        }
      }

      selectOptions.push({text: `${name} (${typeLabel}${extID})`, value: w.uuid})
    })

    setUpdateWarehouseSelectorOptions(selectOptions)
  }, [warehouses, whID])

  useEffect(() => {

    if (!data) {
      toggleItemStatus(code, false)
    }

    // @ts-ignore
    const storedSellerID = data?.configMap.sellerID || ''
    if (storedSellerID) {
      setSellerID(storedSellerID)
      let selectedSeller = {id: storedSellerID, sellerName: ""}
      loadSellerById(parseInt(storedSellerID)).then((res) => {
        if (res) {
          selectedSeller = {id: res.id, sellerName: res.name}
        }
        setSelectedSeller({text: `${storedSellerID} - ${selectedSeller.sellerName}`, value: storedSellerID})
      })
    }

    // @ts-ignore
    setWarehouseID(data?.configMap.warehouseID || '')

    setUpdateFrequency( data?.updateFrequency || 0)

    // @ts-ignore
    setMapBy(data?.configMap.mapBy || defaultMapBy)

    // @ts-ignore
    setStockConvertRules(data?.configMap?.stockConvertRules?.length > 0 ? JSON.parse(data?.configMap?.stockConvertRules) : [initialStockConvertRule])

  }, [data])


  useEffect(() => {
    if (isActive) {
      errors.warehouseID = isValidWarehouseID(warehouseID) ? '' : 'Выберите склад'
    } else {
      errors.warehouseID = ''
      if (!warehouseID) {
        data = null
        updateDataSourceHandler(code, data)
      }
    }
  }, [warehouseID, isActive])

  useEffect(() => {
    if (!data && warehouseID) {
      data = {
        uuid: uuidv4(),
        code: code,
        configMap: {},
        name: title,
        status: isActive ? DataSourceStatus.Current : DataSourceStatus.Archived,
        updateFrequency: 0,
      }
    }

    if (data) {
      data.updateFrequency = updateFrequency
      const rr = stockConvertRules.filter(v => v.operator.length > 0)
      data.configMap = {
        warehouseID: warehouseID,
        sellerID: sellerID.toString(),
        mapBy: mapBy,
        stockConvertRules: JSON.stringify(rr),
      }
      updateDataSourceHandler(code, data)
    }
  }, [warehouseID, sellerID, mapBy, updateFrequency, isActive, stockConvertRules])

  function isValidWarehouseID(warehouseID: string) {
    return warehouseID.length === 36
  }

  const customInputClassName = 'border-[#E2E8F0] mb-1 px-3 py-2 rounded-md text-sm shadow-sm placeholder-slate-400  w-full focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 disabled:bg-slate-50 disabled:text-slate-500 disabled:border-slate-200 disabled:shadow-none'

  function updateSellerID(option: any): void {
    setSelectedSeller(option)
    setSellerID(option.value || option.id)
  }

  function updateWarehouseID(e: any): void {
    setWarehouseID(e.target.value)
  }

  const loadSellers = async (data: string) => {
    const res = await SellerService.getSellersByName(data);
    if(res.data.data) {
      const resData = res.data?.data.map((item: {name: string, id: number}) => {
        return {
          ...item,
          text: `${item.id} - ${item.name}`
        }
      })

      if(!resData) {
        return []
      }

      return resData
    }
    return [];
  }

  const loadSellerById = async (data: number) => {
    const res = await SellerService.getSellerById(data);
    return res.data?.data ?? null;
  }

  function setStockConvertRulesDecorator(rr: StockConvertRule[]) {
    setStockConvertRules(rr)
  }

  function deleteStockConvertRule(position: number) {
    if (position === 0 && stockConvertRules.length === 1) {
      setStockConvertRulesDecorator([initialStockConvertRule])
    } else {
      setStockConvertRulesDecorator(stockConvertRules.filter((_, i) => i !== position))
    }
  }

  function updateStockConvertRules(position: number, data: StockConvertRule) {
    if (position === 0 && stockConvertRules.length === 1) {
      setStockConvertRulesDecorator([data])
    } else {
      setStockConvertRulesDecorator(stockConvertRules.map((v, i) => i === position ? {...data} : v))
    }
  }

  return (
    <div
      className="border-[1.75px] p-2 mt-4 cursor-pointer rounded-[5px] select-none shadow-smooth"
    >
      <div className="flex max-h-11 rounded items-center justify-between" onClick={() => toggleIsDisclosed(code)}>
        <div className="flex items-center min-w-36">
          <div className="text-xs flex flex-row text-[#1E293B] font-medium  text-sm">
            <img src={dataSourceStateIconAssets[state]} className="mr-2" />
            {title}
          </div>
        </div>
        <div className="flex items-center">
          <ToggleButton
            label="Подключен"
            labelId="название-опции-тут"
            onChange={onStatusChange}
            isChecked={isActive}
          />
          <div>
            <div>
              <img src={isDisclosed ? path_up : path_down} alt={''} />
            </div>
          </div>
        </div>

      </div>
      {isDisclosed && (
          <div className={'mt-4'}>
            <label className={`block text-gray-600 text-left font-medium mb-1 text-sm`}>
              {t<string>("Компания", { ns: "messages" })}
            </label>
            <AsyncSelect
                defaultOptions={sellerSelectorOptions}
                selected={selectedSeller}
                CustomMenu={MenuWithButton}
                changeHandler={updateSellerID}
                loadOptions={loadSellers}
                customLabel="text"
                customValue="id"
                theme={`white`}
            />
            <FormSelect
                id="warehouse_warehouseID"
                name="warehouse_warehouseID"
                value={warehouseID}
                label={t<string>("Склад", { ns: "messages" })}
                changeHandler={updateWarehouseID}
                customClassName={customInputClassName}
                customLabelClassName="text-sm mt-4"
                blurHandler={updateWarehouseID}
                validationError={errors.warehouseID.length > 0}
                validationErrorDescription={errors.warehouseID}
                options={updateWarehouseSelectorOptions}
                require={false}
            ></FormSelect>

            <FormSelect
                id="warehouse_mapBy"
                name="warehouse_mapBy"
                value={mapBy}
                label={t<string>("Сопоставить по значению", { ns: "messages" })}
                changeHandler={event => setMapBy(event.target.value)}
                customClassName={classNames(customInputClassName)}
                customLabelClassName="text-sm mt-4"
                blurHandler={event => setMapBy(event.target.value)}
                validationError={false}
                validationErrorDescription={''}
                options={mapBySelectorOptions}
                require={false}
            ></FormSelect>

            <div className="mt-4">
              Правила чтения остатков (приоритет правил - сверху-вниз)
            </div>
            <div className="mt-4">
              {stockConvertRules.map((r, k) =>
                  <RuleRow key={k} position={k} rulesCount={stockConvertRules.length} data={r} deleteRule={deleteStockConvertRule} updateDataHandler={updateStockConvertRules}></RuleRow>
              )}
              <div className={`text-right mt-4`}>
                <Button
                    clickHandler={() => setStockConvertRules([...stockConvertRules, initialStockConvertRule])}
                    appearance="primary"
                    customClassName="button-add"
                    type="button"
                >
                  <div className="flex justify-around w-[150px] items-center">
                    <img src={plus} alt="" className="w-4 h-4" />
                    <div>Добавить правило</div>
                  </div>
                </Button>
              </div>
            </div>

            <div className="mt-4 flex justify-between">
              <div className={"w-5/12"}>
                <FrequencySelector data={updateFrequency} onChange={onUpdateFrequencyChange}></FrequencySelector>
              </div>
              {data &&
                  <button
                      className="self-end items-center text-sm text-left font-medium mb-1 border-[1px] px-[15px] h-[42px] rounded mt-2 active:outline-none"
                      onClick={()=> {data = null; updateDataSourceHandler(code, data); console.log("DATA", data)}}
                      type={"button"}
                  >
                    Удалить источник
                  </button>
              }

            </div>

          </div>
      )}
    </div>
  );
};
